<template>
  <q-card
    class="accounting-error q-mx-sm q-mb-sm shadow-2"
    :class="{'bg-red-9 focused': focused, 'bg-red-7': !focused, 'opaque-80': isResolved && !focused}"
    :dark="darkMode"
  >
    <q-inner-loading
      :showing="loading"
      :dark="darkMode"
      style="z-index: 1;"
    />

    <q-badge
      v-if="isResolved"
      floating
      rounded
      color="green-7"
    >
      <q-icon name="mdi-check" class="q-mr-sm" />
      <q-item-label>Resolved</q-item-label>
    </q-badge>

    <q-expansion-item
      v-model="focused"
      :dark="darkMode"
    >
      <template #header>
        <q-item-section>
          <q-item-label
            v-if="hasLinkedModel"
            caption
          >
            {{ error.model_details.type }}
          </q-item-label>
          <q-item-label
            class="text-bold"
          >
            <div v-html="error.title || 'No error details provided'" />
          </q-item-label>
          <q-item-label
            v-if="error.description"
            caption
          >
            <div v-html="error.description" />
          </q-item-label>
        </q-item-section>
      </template>

      <q-separator :dark="darkMode" />

      <q-list
        padding
        :dark="darkMode"
      >
        <q-item-wrapper
          v-if="hasLinkedModel"
          dense
        >
          <q-item-label class="text-bold">
            {{ error.model_details.name }} <small v-if="error.model_details.reference">({{ error.model_details.reference }})</small>
          </q-item-label>
        </q-item-wrapper>

        <q-item-wrapper
          v-if="canOpenModel"
          :label="openModelStr"
          label-class="text-bold"
          icon="mdi-open-in-new"
          icon-right="mdi-chevron-right"
          clickable
          @click="goTo(error.model_details.url)"
        />

        <q-item-wrapper
          v-if="canResolve"
          icon="mdi-check"
          clickable
          label="Mark as resolved"
          label-class="text-bold"
          :caption="`Once you've fixed the problem, you will need to perform a full sync for this item to correctly sync to ${siteName}`"
          @click="markAsResolved"
        />

        <template v-if="visiblePayload.length > 0">
          <q-separator
            v-if="hasLinkedModel || canOpenModel || canResolve"
            spaced
            :dark="darkMode"
          />

          <q-list>
            <q-item-label header class="q-pb-xs">
              Provider Item Details
            </q-item-label>
            <detail-item
              v-for="line in visiblePayload"
              :key="line.key"
              :name="line.label"
              :value="line.value"
              without-icon
              class="invisible-item"
            />
          </q-list>
        </template>

        <q-item-wrapper
          v-if="!hasAdditionalDetails"
          dense
          label="No additional details to display"
          label-class="text-bold"
        />
      </q-list>
    </q-expansion-item>

    <q-separator :dark="darkMode" />
    <div class="q-py-sm q-px-md text-caption opaque-80 flex justify-between q-gutter-sm">
      <span>{{ $formatSystemDate(error.created_at, luxonFormats.FRIENDLY_DATE_TIME) }}</span>
      <span v-if="error.error_code">Code: {{ error.error_code }}</span>
    </div>
  </q-card>
</template>

<script>
// Display accounting log
export default {
  props: {
    error: {
      type: Object,
      required: true
    },
    resolvable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      focused: false
    }
  },
  computed: {
    darkMode () {
      return true
    },
    isResolved () {
      return this.error.is_resolved
    },
    canResolve () {
      return !this.isResolved && this.resolvable && !this.hasLinkedModel && this.can(this.permissions.canManageProviders)
    },
    hasLinkedModel () {
      return !!this.error.model_details
    },
    canOpenModel () {
      return this.hasLinkedModel && !!this.error.model_details.url
    },
    visiblePayload () {
      return this.error.visible_payload || []
    },
    hasAdditionalDetails () {
      return this.hasLinkedModel || this.canOpenModel || this.canResolve || this.visiblePayload.length > 0
    },
    openModelStr () {
      if (this.isResolved || !this.resolvable) {
        return `View ${this.error.model_details.type}`
      }
      return `View & Fix ${this.error.model_details.type}`
    }
  },
  methods: {
    async markAsResolved () {
      this.loading = true
      const { status } = await this.$post('v1/accounting/logs/error/resolve', {
        id: this.error.id
      })
      this.loading = false
      if (status === 'success') {
        this.error.is_resolved = true
        this.focused = false

        this.$q.notify({
          color: 'green-7',
          message: 'Error successfully marked as resolved'
        })
      }
    }
  }
}
</script>
